import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { mileageAPI, staffAPI } from '../lib/api';
import { formatDate, formatTime, capitalize, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Car,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  FileSpreadsheet,
  TrendingUp,
  AlertCircle,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

export default function Mileage() {
  const { user, canManage } = useAuth();
  const [mileageLogs, setMileageLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [staff, setStaff] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Dialogs
  const [rejectDialog, setRejectDialog] = useState({ open: false, mileageId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [previewDialog, setPreviewDialog] = useState({ open: false, data: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (staffFilter) params.staff_id = staffFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const [logsRes, summaryRes] = await Promise.all([
        mileageAPI.getAll(params),
        mileageAPI.getSummary(params)
      ]);
      
      setMileageLogs(logsRes.data);
      setSummary(summaryRes.data);
      
      // Fetch staff for filter dropdown (admin only)
      if (canManage) {
        const staffRes = await staffAPI.getAll({ include_inactive: false });
        setStaff(staffRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch mileage data:', err);
      toast.error('Failed to load mileage data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, staffFilter, dateFrom, dateTo, canManage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (mileageId) => {
    try {
      await mileageAPI.approve(mileageId, true, null);
      toast.success('Mileage approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await mileageAPI.approve(rejectDialog.mileageId, false, rejectReason);
      toast.success('Mileage rejected');
      setRejectDialog({ open: false, mileageId: null });
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.error('No items selected');
      return;
    }
    try {
      const result = await mileageAPI.bulkApprove(selectedIds);
      toast.success(`Approved ${result.data.approved_count} mileage logs`);
      if (result.data.errors?.length > 0) {
        toast.error(`Some items had errors: ${result.data.errors.length}`);
      }
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to bulk approve');
    }
  };

  const handlePreviewExport = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const res = await mileageAPI.previewExport(params);
      setPreviewDialog({ open: true, data: res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview export');
    }
  };

  const handleExportXero = async () => {
    setExporting(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await mileageAPI.exportXero(params);
      
      // Download the CSV
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mileage_export_xero_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Mileage exported to Xero CSV');
      setPreviewDialog({ open: false, data: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export mileage');
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'exported':
        return <Badge className="bg-blue-100 text-blue-700"><FileSpreadsheet className="w-3 h-3 mr-1" />Exported</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const pendingIds = mileageLogs
      .filter(m => m.status === 'pending' && m.total_km !== null)
      .map(m => m.id);
    
    if (selectedIds.length === pendingIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingIds);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="mileage-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Mileage Tracking</h1>
          <p className="text-muted-foreground">Track and manage vehicle mileage</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handlePreviewExport}
              data-testid="preview-export-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Export
            </Button>
            <Button
              onClick={handleExportXero}
              disabled={exporting}
              className="bg-secondary hover:bg-secondary/90"
              data-testid="export-xero-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export to Xero
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="stat-card" data-testid="stat-total-km">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
                <Car className="w-4 h-4 text-secondary" />
              </div>
            </div>
            <p className="stat-value">{summary.total_km} km</p>
            <p className="stat-label">Total Distance</p>
          </Card>

          <Card className="stat-card" data-testid="stat-total-trips">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <p className="stat-value">{summary.total_trips}</p>
            <p className="stat-label">Total Trips</p>
          </Card>

          <Card className="stat-card" data-testid="stat-pending">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
            </div>
            <p className="stat-value">{summary.pending_count}</p>
            <p className="stat-label">Pending Approval</p>
          </Card>

          <Card className="stat-card" data-testid="stat-approved">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <p className="stat-value">{summary.approved_count}</p>
            <p className="stat-label">Ready to Export</p>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="exported">Exported</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {canManage && (
              <div className="space-y-2">
                <Label>Staff Member</Label>
                <Select value={staffFilter || "all"} onValueChange={(v) => setStaffFilter(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-staff">
                    <SelectValue placeholder="All Staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {staff.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="filter-date-from"
              />
            </div>

            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="filter-date-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {canManage && selectedIds.length > 0 && (
        <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-4 flex items-center justify-between">
          <span className="font-medium text-secondary">
            {selectedIds.length} item(s) selected
          </span>
          <Button onClick={handleBulkApprove} className="bg-emerald-600 hover:bg-emerald-700">
            <CheckCircle className="w-4 h-4 mr-2" />
            Approve Selected
          </Button>
        </div>
      )}

      {/* Mileage Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading">Mileage Logs</CardTitle>
            {canManage && mileageLogs.some(m => m.status === 'pending' && m.total_km !== null) && (
              <Button variant="ghost" size="sm" onClick={toggleSelectAll}>
                {selectedIds.length === mileageLogs.filter(m => m.status === 'pending' && m.total_km !== null).length
                  ? 'Deselect All'
                  : 'Select All Pending'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {mileageLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    {canManage && <th className="text-left p-3 w-10"></th>}
                    <th className="text-left p-3">Date</th>
                    {canManage && <th className="text-left p-3">Staff</th>}
                    <th className="text-left p-3">Start</th>
                    <th className="text-left p-3">End</th>
                    <th className="text-left p-3">Total KM</th>
                    <th className="text-left p-3">Status</th>
                    {canManage && <th className="text-left p-3">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {mileageLogs.map(log => (
                    <tr 
                      key={log.id} 
                      className="border-b hover:bg-muted/50"
                      data-testid={`mileage-row-${log.id}`}
                    >
                      {canManage && (
                        <td className="p-3">
                          {log.status === 'pending' && log.total_km !== null && (
                            <Checkbox
                              checked={selectedIds.includes(log.id)}
                              onCheckedChange={() => toggleSelect(log.id)}
                            />
                          )}
                        </td>
                      )}
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{formatDate(log.created_at)}</p>
                          <p className="text-xs text-muted-foreground">{formatTime(log.created_at)}</p>
                        </div>
                      </td>
                      {canManage && (
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{log.staff_name || 'Unknown'}</p>
                            {log.client_name && (
                              <p className="text-xs text-muted-foreground">{log.client_name}</p>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="p-3 font-mono">{log.start_odometer?.toLocaleString()}</td>
                      <td className="p-3 font-mono">
                        {log.end_odometer ? log.end_odometer.toLocaleString() : '-'}
                      </td>
                      <td className="p-3">
                        {log.total_km !== null ? (
                          <span className="font-semibold text-secondary">{log.total_km} km</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3">{getStatusBadge(log.status)}</td>
                      {canManage && (
                        <td className="p-3">
                          {log.status === 'pending' && log.total_km !== null && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => handleApprove(log.id)}
                                data-testid={`approve-btn-${log.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setRejectDialog({ open: true, mileageId: log.id })}
                                data-testid={`reject-btn-${log.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          {log.rejection_reason && (
                            <p className="text-xs text-red-600 mt-1">{log.rejection_reason}</p>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No mileage logs found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, mileageId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Mileage Log</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this mileage log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, mileageId: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Preview Dialog */}
      <Dialog open={previewDialog.open} onOpenChange={(open) => !open && setPreviewDialog({ open: false, data: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Preview</DialogTitle>
            <DialogDescription>
              Review what will be exported to Xero
            </DialogDescription>
          </DialogHeader>
          {previewDialog.data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">Records to Export</p>
                  <p className="text-2xl font-bold">{previewDialog.data.record_count}</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">Total Distance</p>
                  <p className="text-2xl font-bold">{previewDialog.data.total_km} km</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">Rate per KM</p>
                  <p className="text-2xl font-bold">${previewDialog.data.km_rate}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-sm text-emerald-600">Total Amount</p>
                  <p className="text-2xl font-bold text-emerald-700">${previewDialog.data.total_amount}</p>
                </div>
              </div>
              {previewDialog.data.record_count === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">No records to export</p>
                    <p className="text-sm text-amber-700">Approve pending mileage logs first to include them in the export.</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialog({ open: false, data: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleExportXero} 
              disabled={exporting || previewDialog.data?.record_count === 0}
              className="bg-secondary hover:bg-secondary/90"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
